import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Avatar,
  Stack,
} from "@mui/material";
import { GridExpandMoreIcon } from "@mui/x-data-grid";
import type { ReactNode } from "react";

import { GenericTable } from "@/components/GenericTable/GenericTable";
import type { GenericTableProps } from "@/components/GenericTable/types";
import { usePreferMobileCards } from "../../context/EhrRecordsLayoutContext";
import { EhrSummaryMobileList } from "./EhrSummaryMobileList";

type EhrMobileGenericSectionProps<T extends Record<string, any>> = {
  title: string;
  columns: GenericTableProps<T>["tableColumns"];
  rows: T[];
  actions?: GenericTableProps<T>["actions"];
  characterLimit?: number;
  statusVariant?: GenericTableProps<T>["statusVariant"];
  /** Conteúdo extra após a tabela no web (ex.: modais locais). */
  footer?: ReactNode;
};

/**
 * Seção do sumário: Accordion + GenericTable no web;
 * cards + preview 5 + Ver tudo no mobile.
 */
export function EhrMobileGenericSection<T extends Record<string, any>>({
  title,
  columns,
  rows,
  actions,
  characterLimit,
  statusVariant,
  footer,
}: EhrMobileGenericSectionProps<T>) {
  const isMobileLayout = usePreferMobileCards();
  const count = rows?.length ?? 0;

  if (isMobileLayout) {
    return (
      <>
        <EhrSummaryMobileList
          title={title}
          columns={columns}
          rows={rows ?? []}
          actions={actions}
        />
        {footer}
      </>
    );
  }

  return (
    <>
      <Accordion sx={{ width: "100%" }} elevation={5}>
        <AccordionSummary
          expandIcon={<GridExpandMoreIcon />}
          aria-controls={`panel-${title}`}
          id={`panel-header-${title}`}
        >
          <Stack direction="row" alignItems="center" gap={1}>
            {title}
            <Avatar
              sx={{
                width: 26,
                height: 26,
                fontSize: "14px",
                fontWeight: 600,
                backgroundColor: "primary100.main",
                color: "primary1000.main",
              }}
            >
              {count || "0"}
            </Avatar>
          </Stack>
        </AccordionSummary>

        <AccordionDetails>
          <GenericTable
            tableColumns={columns}
            tableRows={rows ?? []}
            actions={actions}
            characterLimit={characterLimit}
            statusVariant={statusVariant}
          />
        </AccordionDetails>
      </Accordion>
      {footer}
    </>
  );
}
